import React from 'react';
import { Link } from 'react-router-dom';
import {
  PiArrowRight,
  PiBatteryCharging,
  PiBellRinging,
  PiBriefcase,
  PiDrop,
  PiCheckCircle,
  PiDeviceMobile,
  PiIdentificationCard,
  PiPlus,
  PiReceipt,
  PiSuitcaseRolling,
  PiUsers,
  PiWifiHigh,
} from 'react-icons/pi';
import SiteHeader from '../SiteHeader';
import SiteFooter from '../SiteFooter';
import { PAGE_META, usePageMeta, useReveal } from '../hooks';
import { Button, Container, Eyebrow, SectionHeading } from '../ui';
import { CONTAINER_WIDTH, HALF_WIDTH, HeroImage, THIRD_WIDTH, siteImage } from '../images';
import { APP_STORE_URL, PLAY_STORE_URL } from '../appLinks';
import { AIRPORT_ROUTES, CITY_PAGES, SERVICE_PAGES } from './landing';
import { VEHICLES, linkPrice } from './landing/prices';
import { FLEET_CARS, carAlt } from '../fleet';
import { fillPrices } from '../fillPrices';
import { PHONE_DISPLAY, PHONE_HREF } from './InfoPage';
import './home.css';

const SERVICES = [
  {
    title: 'Airport transfers',
    body: 'JFK, LaGuardia and Newark. Add your flight number and your chauffeur plans around it.',
    image: 'airport-terminal',
    to: '/airport-transfers',
    cta: 'Airport rides',
  },
  {
    title: 'Hourly chauffeur',
    body: 'A car and chauffeur at your disposal for meetings, events or an evening across the city.',
    image: 'night-ride-rear-seat',
    to: '/hourly-chauffeur-service',
    cta: 'Hourly prices',
  },
  {
    title: 'Point to point',
    body: 'From your door to wherever you need to be, with the price shown before you book.',
    image: 'manhattan-street-night',
    to: '/point-to-point-car-service',
    cta: 'Point-to-point prices',
  },
];

const EXPERIENCE = [
  [PiIdentificationCard, 'Your chauffeur, by name', 'See who is driving you, the vehicle and its plate as soon as a chauffeur is assigned.'],
  [PiBellRinging, 'Live ride updates', 'Know when your chauffeur is on the way and when they have arrived.'],
  [PiReceipt, 'Upfront pricing', 'Your total is shown before you confirm, so you know exactly what you will pay.'],
  [PiCheckCircle, 'Easy changes', 'Edit or cancel your booking from your account on the web or in the app.'],
];

const INCLUDED = [
  [PiDrop, 'Complimentary water'],
  [PiBatteryCharging, 'Phone charger'],
  [PiWifiHigh, 'Wi-Fi'],
  [PiBriefcase, 'Help with your bags'],
];

const STEPS = [
  ['Choose your service', 'Airport transfer, hourly or point to point, and where you are going.'],
  ['Pick a time and vehicle', 'Select your pickup time in New York time and the vehicle that suits you.'],
  ['Ride in comfort', 'Pay securely, then follow your chauffeur live until you arrive.'],
];

// Prices come from prices.json so the answers always match the price pages.
const price = (path) => fillPrices(`{{price:${path}}}`);

const FAQ = [
  {
    q: 'How much does a chauffeur service in New York City cost?',
    a: `Airport rides to anywhere in Manhattan are a flat ${price('routes.jfk-manhattan.sedan')} from JFK, ${price('routes.lga-manhattan.sedan')} from LaGuardia and ${price('routes.ewr-manhattan.sedan')} from Newark in a Sedan. An hourly chauffeur is ${price('hourly.sedan.perHour')} per hour with a 2-hour minimum, and point-to-point rides start at ${price('pointRates.sedan.minimum')}. Tolls are included and your exact total is shown before you pay.`,
  },
  {
    q: 'Which areas do you cover?',
    a: 'We pick up anywhere in Manhattan, Brooklyn, Queens, the Bronx and Staten Island, and at JFK, LaGuardia and Newark airports. We also drive from New York City to the Hamptons, Long Island, Westchester, New Jersey, Connecticut, Philadelphia, Boston, Washington DC and Atlantic City.',
  },
  {
    q: 'Do you track my flight for airport pickups?',
    a: 'Yes. Add your flight number when you book and your chauffeur follows your flight. If it lands early or late they adjust, and the first 60 minutes of waiting after landing are free.',
  },
  {
    q: 'Can I book a chauffeur by the hour?',
    a: `Yes. Hourly service starts at ${price('hourly.sedan.perHour')} per hour with a 2-hour minimum. Your car and chauffeur stay with you for meetings, shopping, sightseeing, events or a night out, with as many stops as you like.`,
  },
  {
    q: 'Can I change or cancel my booking?',
    a: 'Yes. Rides can be changed or cancelled up to 1 hour before pickup from your account on the website or in the app. For a last-minute change, call us.',
  },
  {
    q: 'How do I book?',
    a: 'Book online in a few minutes or in the RoolDrive app for iPhone and Android. Choose your service, pickup time and vehicle, see your price, and pay securely. You can also call us 24/7.',
  },
];

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
};

export default function HomePage() {
  usePageMeta(PAGE_META['/'].title, PAGE_META['/'].description);
  const revealRef = useReveal();

  return (
    <div className="lx-page" ref={revealRef}>
      <SiteHeader overlay />

      <main>
        <section className="hm-hero">
          <div className="hm-hero-media">
            <HeroImage name="hero-manhattan-night" />
          </div>
          <Container>
            <div className="hm-hero-content">
              <Eyebrow>Arrive in quiet luxury</Eyebrow>
              <h1 className="lx-display">New York City <em>chauffeur service</em></h1>
              <p className="lx-lead">
                RoolDrive is a private chauffeur service in New York City. Professional chauffeurs and immaculate
                vehicles for JFK, LaGuardia and Newark transfers, hourly service and rides across the city, booked
                online or in the app.
              </p>
              <div className="hm-hero-actions">
                <Button to="/book" size="lg" iconAfter={PiArrowRight}>Book a ride</Button>
                <Button href="#app" variant="secondary" size="lg" icon={PiDeviceMobile}>
                  Get the app
                </Button>
              </div>
              <div className="hm-hero-facts">
                <span><PiReceipt aria-hidden /> Upfront pricing</span>
                <span><PiIdentificationCard aria-hidden /> Professional chauffeurs</span>
                <span><PiBellRinging aria-hidden /> Live ride updates</span>
              </div>
            </div>
          </Container>
        </section>

        <section className="lx-section">
          <Container>
            <SectionHeading
              eyebrow="Services"
              title={<>Every journey, <em>handled</em></>}
              lead="Whether you are catching a flight or need a car for the evening, your chauffeur is ready when you are."
              className="lx-reveal"
            />
            <div className="hm-services">
              {SERVICES.map((s) => (
                <Link key={s.title} to={s.to} className="hm-service lx-reveal">
                  <img {...siteImage(s.image, THIRD_WIDTH)} alt="" loading="lazy" />
                  <h3 className="lx-h3">{s.title}</h3>
                  <p>{s.body}</p>
                  <span className="hm-service-link">{s.cta || 'Book now'} <PiArrowRight aria-hidden /></span>
                </Link>
              ))}
            </div>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <div className="lx-reveal" style={{ maxWidth: 820, display: 'grid', gap: 16 }}>
              <SectionHeading
                eyebrow="Chauffeur service in New York City"
                title={<>A private car and driver, <em>wherever you are going</em></>}
              />
              <p className="pg-text">
                Getting around New York should be the easy part of your day. With RoolDrive, a professional chauffeur
                meets you at your door, your hotel or the arrivals hall, helps with your bags and drives you directly
                to where you need to be, in a clean, comfortable Sedan or SUV.
              </p>
              <p className="pg-text">
                Our chauffeurs cover Manhattan, Brooklyn, Queens, the Bronx and Staten Island, with{' '}
                <Link to="/jfk-car-service">JFK</Link>, <Link to="/laguardia-car-service">LaGuardia</Link> and{' '}
                <Link to="/newark-airport-car-service">Newark airport car service</Link> at flat rates to Manhattan.
                Book an <Link to="/hourly-chauffeur-service">hourly chauffeur</Link> for business meetings, roadshows,
                shopping, sightseeing, weddings and nights out, or a{' '}
                <Link to="/point-to-point-car-service">point-to-point ride</Link> anywhere in the city. For longer
                trips, we drive from New York City to <Link to="/nyc-to-the-hamptons-car-service">the Hamptons</Link>,
                Connecticut, New Jersey, <Link to="/nyc-to-philadelphia-car-service">Philadelphia</Link>,{' '}
                <Link to="/nyc-to-boston-car-service">Boston</Link> and{' '}
                <Link to="/nyc-to-washington-dc-car-service">Washington DC</Link>.
              </p>
              <p className="pg-text">
                Whichever service you choose, the price is shown before you book, tolls are included, and you can see
                your chauffeur's name, vehicle and plate as soon as they are assigned.
              </p>
            </div>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container className="hm-split">
            <div className="hm-collage lx-reveal">
              <img className="hm-collage-main" {...siteImage('cabin-skyline-view', HALF_WIDTH)} alt="The driver's seat of a luxury SUV looking out at the Manhattan skyline" loading="lazy" />
              <img className="hm-collage-detail" {...siteImage('leather-seat-detail', THIRD_WIDTH)} alt="Leather seat detail in a luxury vehicle" loading="lazy" />
            </div>
            <div className="lx-reveal">
              <SectionHeading
                eyebrow="The RoolDrive experience"
                title={<>Calm, punctual, <em>personal</em></>}
                lead="Everything you need to know about your ride, before and during the journey."
              />
              <ul className="hm-features">
                {EXPERIENCE.map(([Icon, title, body]) => (
                  <li key={title} className="hm-feature">
                    <span className="hm-feature-icon"><Icon aria-hidden /></span>
                    <div>
                      <h3>{title}</h3>
                      <p>{body}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <SectionHeading
              eyebrow="Included with every ride"
              title={<>Small comforts, <em>always</em></>}
              lead="Every ride comes with these at no extra charge."
              className="lx-reveal"
            />
            <ul className="hm-included">
              {INCLUDED.map(([Icon, label]) => (
                <li key={label} className="hm-included-item lx-reveal">
                  <span className="hm-feature-icon"><Icon aria-hidden /></span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <SectionHeading
              eyebrow="The fleet"
              title={<>Choose your <em>vehicle</em></>}
              lead="Black Cadillacs for every occasion, from a quiet airport transfer to a night out with friends."
              className="lx-reveal"
            />
            <div className="lx-grid" style={{ '--lx-min': '240px' }}>
              {FLEET_CARS.map((car) => {
                const v = VEHICLES[car.classKey];
                return (
                  <Link key={car.id} to="/fleet" className="lx-card lx-card--interactive hm-fleet-card lx-reveal" style={{ color: 'inherit', textDecoration: 'none' }}>
                    <div className="hm-fleet-photo">
                      <img src={car.image} alt={carAlt(car, v.name)} width="960" height="480" loading="lazy" />
                    </div>
                    <div className="hm-fleet-body">
                      <Eyebrow>{v.name}</Eyebrow>
                      <h3 className="lx-h3">{car.name}</h3>
                      <div className="hm-fleet-meta">
                        <span><PiUsers aria-hidden /> {v.passengers} passengers</span>
                        <span><PiSuitcaseRolling aria-hidden /> {v.luggage} bags</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="lx-reveal" style={{ marginTop: 28 }}>
              <Button to="/fleet" variant="secondary" iconAfter={PiArrowRight}>See the fleet</Button>
            </div>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <SectionHeading eyebrow="How it works" title={<>Booked in <em>three steps</em></>} className="lx-reveal" />
            <div className="hm-steps">
              {STEPS.map(([title, body], i) => (
                <div key={title} className="hm-step lx-reveal">
                  <div className="hm-step-num">0{i + 1}</div>
                  <h3 className="lx-h3">{title}</h3>
                  <p>{body}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <SectionHeading
              eyebrow="Prices and popular rides"
              title={<>Black car service, <em>priced upfront</em></>}
              lead="Flat rates between the airports and Manhattan, chauffeurs by the hour and private rides to the cities around New York."
              className="lx-reveal"
            />
            <div className="hm-popular lx-reveal">
              {[
                ['Airports', AIRPORT_ROUTES],
                ['Services', SERVICE_PAGES],
                ['From NYC to', CITY_PAGES],
              ].map(([label, pages]) => (
                <div key={label}>
                  <Eyebrow>{label}</Eyebrow>
                  <ul>
                    {pages.map((p) => (
                      <li key={p.path}>
                        <Link to={p.path}>{p.linkText}</Link>
                        <span>{linkPrice(p)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="hm-popular-note">
              Flat rates between the airports and Manhattan are fixed. Other prices are estimates and can be different
              when you book, depending on your exact pickup and drop-off, distance, route, traffic and time. All prices
              include tolls, and your exact price is shown before you pay.
            </p>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <div id="app" className="hm-app lx-reveal">
              <div className="hm-app-media">
                <img {...siteImage('app-phones', HALF_WIDTH)} alt="The RoolDrive app: booking a ride from JFK and the home screen" loading="lazy" />
              </div>
              <div className="hm-app-content">
                <img className="hm-app-icon" src="/images/site/app-icon.webp" width="68" height="68" alt="" loading="lazy" />
                <Eyebrow>The RoolDrive app</Eyebrow>
                <h2 className="lx-h2">Book your <em>next ride</em></h2>
                <p className="hm-app-lead">Choose a route and ride that fits.</p>
                <ul className="hm-app-list">
                  <li><PiCheckCircle aria-hidden /> Book and pay in a few taps</li>
                  <li><PiCheckCircle aria-hidden /> Notifications when your chauffeur is on the way</li>
                  <li><PiCheckCircle aria-hidden /> Call or message your chauffeur directly</li>
                </ul>
                <div className="hm-app-stores">
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="hm-store-badge">
                    <img src="/images/site/badge-google-play.webp" width="150" height="44" alt="Get it on Google Play" loading="lazy" />
                  </a>
                  <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="hm-store-badge">
                    <img src="/images/site/badge-app-store.webp" width="130" height="44" alt="Download on the App Store" loading="lazy" />
                  </a>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <div className="pg-faq-group lx-reveal" style={{ maxWidth: 820 }}>
              <h2 className="lx-h2">Chauffeur service in New York City: <em>questions</em></h2>
              {FAQ.map(({ q, a }) => (
                <details key={q} className="pg-faq">
                  <summary>{q}<PiPlus aria-hidden /></summary>
                  <p>{a}</p>
                </details>
              ))}
              <p className="pg-text" style={{ marginTop: 20 }}>
                More answers in our <Link to="/help">help center</Link>, or call us 24/7 on{' '}
                <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>.
              </p>
            </div>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA).replace(/</g, '\\u003c') }} />
          </Container>
        </section>

        <section className="lx-section" style={{ paddingTop: 0 }}>
          <Container>
            <div className="hm-band hm-band--center lx-reveal">
              <img {...siteImage('williamsburg-bridge-dusk', CONTAINER_WIDTH)} alt="" loading="lazy" />
              <div className="hm-band-content">
                <Eyebrow>Ready when you are</Eyebrow>
                <h2 className="lx-h2">Where can we <em>take you</em>?</h2>
                <Button to="/book" size="lg" iconAfter={PiArrowRight}>Book a ride</Button>
                <p className="hm-phone">Prefer to talk? Call <a href="tel:+19292315951">+1 929-231-5951</a></p>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
